import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

// Modal Base
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = "",
  ...props
}) => {
  // Fechar modal com ESC
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Prevenir scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const sizeClasses = {
    sm: "modal-sm",
    md: "modal-md",
    lg: "modal-lg",
    xl: "modal-xl",
    full: "modal-full",
  };

  return createPortal(
    <div className={`modal-overlay ${className}`} onClick={handleOverlayClick}>
      <div className={`modal-content ${sizeClasses[size]}`}>
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h3 className="modal-title">{title}</h3>}
            {showCloseButton && (
              <button
                className="modal-close-btn"
                onClick={onClose}
                type="button"
                aria-label="Fechar"
              >
                <Icon name="close" size={20} />
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

// Modal de Confirmação
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmar Ação",
  message = "Tem certeza que deseja continuar?",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  variant = "primary", // primary, danger, warning
  loading = false,
  ...props
}) => {
  const handleConfirm = async () => {
    if (loading) return;
    await onConfirm();
  };

  const getIcon = () => {
    switch (variant) {
      case "danger":
        return <Icon name="alert" size={24} color="var(--color-danger)" />;
      case "warning":
        return <Icon name="warning" size={24} color="var(--color-warning)" />;
      default:
        return <Icon name="help" size={24} color="var(--color-primary)" />;
    }
  };

  const getButtonClass = () => {
    switch (variant) {
      case "danger":
        return "btn btn-danger";
      case "warning":
        return "btn btn-warning";
      default:
        return "btn btn-primary";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" {...props}>
      <div className="text-center">
        <div className="mb-3">{getIcon()}</div>
        <p className="mb-4">{message}</p>
        <div className="d-flex gap-2 justify-content-center">
          <button
            className="btn btn-outline"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={getButtonClass()}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Icon name="refresh" size={16} className="spinner" />
                Processando...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Modal de Alerta/Notificação
export const AlertModal = ({
  isOpen,
  onClose,
  title,
  message,
  variant = "info", // success, error, warning, info
  buttonText = "OK",
  ...props
}) => {
  const getConfig = () => {
    switch (variant) {
      case "success":
        return {
          icon: (
            <Icon name="check-circle" size={32} color="var(--color-success)" />
          ),
          title: title || "Sucesso!",
          bgColor: "var(--color-success)",
        };
      case "error":
        return {
          icon: <Icon name="error" size={32} color="var(--color-danger)" />,
          title: title || "Erro!",
          bgColor: "var(--color-danger)",
        };
      case "warning":
        return {
          icon: <Icon name="warning" size={32} color="var(--color-warning)" />,
          title: title || "Atenção!",
          bgColor: "var(--color-warning)",
        };
      default:
        return {
          icon: <Icon name="info" size={32} color="var(--color-info)" />,
          title: title || "Informação",
          bgColor: "var(--color-info)",
        };
    }
  };

  const config = getConfig();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      size="sm"
      {...props}
    >
      <div className="text-center">
        <div className="mb-3">{config.icon}</div>
        <div className="mb-4">
          {typeof message === "string" ? <p>{message}</p> : message}
        </div>
        <button className="btn btn-primary" onClick={onClose}>
          {buttonText}
        </button>
      </div>
    </Modal>
  );
};

// Modal de Loading
export const LoadingModal = ({
  isOpen,
  message = "Carregando...",
  ...props
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Não pode fechar modal de loading
      showCloseButton={false}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      size="sm"
      {...props}
    >
      <div className="text-center py-4">
        <div className="mb-3">
          <Icon
            name="refresh"
            size={32}
            className="spinner"
            color="var(--color-primary)"
          />
        </div>
        <p className="mb-0">{message}</p>
      </div>
    </Modal>
  );
};

// Modal de Input/Prompt
export const InputModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Digite o valor",
  label,
  placeholder = "",
  initialValue = "",
  inputType = "text",
  required = false,
  submitText = "Confirmar",
  cancelText = "Cancelar",
  loading = false,
  ...props
}) => {
  const [value, setValue] = React.useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (required && !value.trim()) return;
    await onSubmit(value);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" {...props}>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          {label && <label className="form-label">{label}</label>}
          <input
            type={inputType}
            className="form-control"
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required={required}
            autoFocus
          />
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || (required && !value.trim())}
          >
            {loading ? (
              <>
                <Icon name="refresh" size={16} className="spinner" />
                Processando...
              </>
            ) : (
              submitText
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// Hook para usar modais
export const useModal = () => {
  const [modalState, setModalState] = React.useState({
    confirm: { isOpen: false, props: {} },
    alert: { isOpen: false, props: {} },
    loading: { isOpen: false, props: {} },
    input: { isOpen: false, props: {} },
  });

  const showConfirm = (props) => {
    return new Promise((resolve) => {
      setModalState((prev) => ({
        ...prev,
        confirm: {
          isOpen: true,
          props: {
            ...props,
            onConfirm: async () => {
              if (props.onConfirm) {
                await props.onConfirm();
              }
              setModalState((prev) => ({
                ...prev,
                confirm: { isOpen: false, props: {} },
              }));
              resolve(true);
            },
            onClose: () => {
              setModalState((prev) => ({
                ...prev,
                confirm: { isOpen: false, props: {} },
              }));
              resolve(false);
            },
          },
        },
      }));
    });
  };

  const showAlert = (props) => {
    return new Promise((resolve) => {
      setModalState((prev) => ({
        ...prev,
        alert: {
          isOpen: true,
          props: {
            ...props,
            onClose: () => {
              setModalState((prev) => ({
                ...prev,
                alert: { isOpen: false, props: {} },
              }));
              resolve();
            },
          },
        },
      }));
    });
  };

  const showLoading = (props = {}) => {
    setModalState((prev) => ({
      ...prev,
      loading: {
        isOpen: true,
        props,
      },
    }));
  };

  const hideLoading = () => {
    setModalState((prev) => ({
      ...prev,
      loading: { isOpen: false, props: {} },
    }));
  };

  const showInput = (props) => {
    return new Promise((resolve) => {
      setModalState((prev) => ({
        ...prev,
        input: {
          isOpen: true,
          props: {
            ...props,
            onSubmit: async (value) => {
              if (props.onSubmit) {
                await props.onSubmit(value);
              }
              setModalState((prev) => ({
                ...prev,
                input: { isOpen: false, props: {} },
              }));
              resolve(value);
            },
            onClose: () => {
              setModalState((prev) => ({
                ...prev,
                input: { isOpen: false, props: {} },
              }));
              resolve(null);
            },
          },
        },
      }));
    });
  };

  const ModalComponents = () => (
    <>
      <ConfirmModal
        {...modalState.confirm.props}
        isOpen={modalState.confirm.isOpen}
      />
      <AlertModal
        {...modalState.alert.props}
        isOpen={modalState.alert.isOpen}
      />
      <LoadingModal
        {...modalState.loading.props}
        isOpen={modalState.loading.isOpen}
      />
      <InputModal
        {...modalState.input.props}
        isOpen={modalState.input.isOpen}
      />
    </>
  );

  return {
    showConfirm,
    showAlert,
    showLoading,
    hideLoading,
    showInput,
    ModalComponents,
  };
};

export default Modal;
